import { ChevronDown, Check } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/flexkit/Button";
import { TimePicker } from "@/components/flexkit/TimePicker";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { Input } from "@/components/podkit/forms/Input";
import { cn } from "@/components/podkit/lib/cn";

// eslint-disable-next-line react-refresh/only-export-components
export enum CronFrequency {
    Hour = "hour",
    Day = "day",
    Week = "week",
    Month = "month",
}

export type CronSelectorProps = {
    value?: string;
    onChange?: (cronString: string) => void;
    className?: string;
    showTimezone?: boolean;
    readOnly?: boolean;
    /** When true, the frequency dropdown is hidden (controlled externally) */
    hideFrequency?: boolean;
};

type CronConfig = {
    frequency: CronFrequency;
    minute?: number;
    hour?: number;
    dayOfMonth?: number;
    month?: number;
    dayOfWeek?: number;
};

// eslint-disable-next-line react-refresh/only-export-components
export const FREQUENCIES = new Map<CronFrequency, string>([
    [CronFrequency.Hour, "Every hour"],
    [CronFrequency.Day, "Every day"],
    [CronFrequency.Week, "Every week"],
    [CronFrequency.Month, "Every month"],
]);

const WEEKDAYS = new Map<number, string>([
    [0, "Sunday"],
    [1, "Monday"],
    [2, "Tuesday"],
    [3, "Wednesday"],
    [4, "Thursday"],
    [5, "Friday"],
    [6, "Saturday"],
]);

// eslint-disable-next-line react-refresh/only-export-components
export const parseCronString = (cronString: string): CronConfig | null => {
    const parts = cronString.trim().split(/\s+/);
    if (parts.length !== 5) return null;

    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

    // Every hour: X * * * *
    if (minute !== "*" && hour === "*" && dayOfMonth === "*" && month === "*" && dayOfWeek === "*") {
        return { frequency: CronFrequency.Hour, minute: parseInt(minute) };
    }

    // Every day: X Y * * *
    if (minute !== "*" && hour !== "*" && dayOfMonth === "*" && month === "*" && dayOfWeek === "*") {
        return { frequency: CronFrequency.Day, minute: parseInt(minute), hour: parseInt(hour) };
    }

    // Every week: X Y * * Z
    if (minute !== "*" && hour !== "*" && dayOfMonth === "*" && month === "*" && dayOfWeek !== "*") {
        return {
            frequency: CronFrequency.Week,
            minute: parseInt(minute),
            hour: parseInt(hour),
            dayOfWeek: parseInt(dayOfWeek),
        };
    }

    // Every month: X Y Z * *
    if (minute !== "*" && hour !== "*" && dayOfMonth !== "*" && month === "*" && dayOfWeek === "*") {
        return {
            frequency: CronFrequency.Month,
            minute: parseInt(minute),
            hour: parseInt(hour),
            dayOfMonth: parseInt(dayOfMonth),
        };
    }

    return null;
};

// eslint-disable-next-line react-refresh/only-export-components
export const generateCronString = (config: CronConfig): string => {
    const { frequency, minute = 0, hour = 0, dayOfMonth = 1, dayOfWeek = 0 } = config;

    switch (frequency) {
        case CronFrequency.Hour:
            return `${minute} * * * *`;
        case CronFrequency.Day:
            return `${minute} ${hour} * * *`;
        case CronFrequency.Week:
            return `${minute} ${hour} * * ${dayOfWeek}`;
        case CronFrequency.Month:
            return `${minute} ${hour} ${dayOfMonth} * *`;
        default:
            return `${minute} * * * *`;
    }
};

const LocalTimeDisplay: React.FC<{
    frequency: CronFrequency;
    hour: number;
    minute: number;
    showTimezone: boolean;
}> = ({ frequency, hour, minute, showTimezone }) => {
    if (!showTimezone) return null;

    // Only show timezone for frequencies that have specific times (not "every hour")
    if (frequency === CronFrequency.Hour) return null;

    // Get timezone offset in minutes
    const timezoneOffset = new Date().getTimezoneOffset();
    const isUTC = timezoneOffset === 0;

    // If already in UTC, no need to show conversion
    if (isUTC) return null;

    // Calculate local time
    const utcDate = new Date();
    utcDate.setUTCHours(hour, minute, 0, 0);

    const localHour = utcDate.getHours();
    const localMinute = utcDate.getMinutes();

    const formatTime = (h: number, m: number) => `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

    // Get timezone name
    const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return (
        <span className="text-sm text-content-secondary">
            ({formatTime(localHour, localMinute)} {timezoneName})
        </span>
    );
};

export const CronSelector: React.FC<CronSelectorProps> = ({
    value,
    onChange,
    className,
    showTimezone = true,
    readOnly = false,
    hideFrequency = false,
}) => {
    const parseValue = (v: string | undefined): CronConfig => {
        if (v) {
            const parsed = parseCronString(v);
            if (parsed) return parsed;
        }
        return { frequency: CronFrequency.Hour, minute: 0 };
    };

    const [config, setConfig] = React.useState<CronConfig>(() => parseValue(value));

    // Track the last cron string we emitted to avoid re-parsing our own output
    const lastEmittedRef = React.useRef<string>(value ?? "");

    // Sync config from external value changes (e.g. frequency changed in parent)
    React.useEffect(() => {
        if (value !== undefined && value !== lastEmittedRef.current) {
            setConfig(parseValue(value));
            lastEmittedRef.current = value;
        }
    }, [value]);

    // Local state for number inputs to allow empty values while typing
    const [minuteInput, setMinuteInput] = React.useState(String(config.minute ?? 0));
    const [dayOfMonthInput, setDayOfMonthInput] = React.useState(String(config.dayOfMonth ?? 1));

    // Track focus state to avoid overwriting user input
    const [minuteFocused, setMinuteFocused] = React.useState(false);
    const [dayOfMonthFocused, setDayOfMonthFocused] = React.useState(false);

    // Sync local input state when config changes (but not while focused)
    React.useEffect(() => {
        if (!minuteFocused) {
            setMinuteInput(String(config.minute ?? 0));
        }
    }, [config.minute, minuteFocused]);

    React.useEffect(() => {
        if (!dayOfMonthFocused) {
            setDayOfMonthInput(String(config.dayOfMonth ?? 1));
        }
    }, [config.dayOfMonth, dayOfMonthFocused]);

    React.useEffect(() => {
        const cronString = generateCronString(config);
        lastEmittedRef.current = cronString;
        onChange?.(cronString);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config]);

    const updateConfig = (updates: Partial<CronConfig>) => {
        setConfig((prev) => ({ ...prev, ...updates }));
    };

    const handleFrequencyChange = (frequency: CronFrequency) => {
        setConfig({
            frequency,
            minute: 0,
            hour: 0,
            dayOfMonth: 1,
            month: 1,
            dayOfWeek: 0,
        });
        // Reset local input state when frequency changes
        setMinuteInput("0");
        setDayOfMonthInput("1");
    };

    const handleMinuteInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        // Allow empty or numeric input
        if (newValue === "" || /^\d{0,2}$/.test(newValue)) {
            setMinuteInput(newValue);
        }
    };

    const handleMinuteInputBlur = () => {
        setMinuteFocused(false);
        let val = parseInt(minuteInput);
        if (isNaN(val) || val < 0) {
            val = 0;
        } else if (val > 59) {
            val = 59;
        }
        setMinuteInput(String(val));
        updateConfig({ minute: val });
    };

    const handleDayOfMonthInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        // Allow empty or numeric input
        if (newValue === "" || /^\d{0,2}$/.test(newValue)) {
            setDayOfMonthInput(newValue);
        }
    };

    const handleDayOfMonthInputBlur = () => {
        setDayOfMonthFocused(false);
        let val = parseInt(dayOfMonthInput);
        if (isNaN(val) || val < 1) {
            val = 1;
        } else if (val > 31) {
            val = 31;
        }
        setDayOfMonthInput(String(val));
        updateConfig({ dayOfMonth: val });
    };

    return (
        <div className={cn(className)}>
            <div className="flex flex-wrap items-center gap-2">
                {!hideFrequency &&
                    (readOnly ? (
                        <span className="text-sm font-medium">{FREQUENCIES.get(config.frequency) ?? "Every hour"}</span>
                    ) : (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 border border-border-base">
                                    <span>{FREQUENCIES.get(config.frequency) ?? "Every hour"}</span>
                                    <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                                {Array.from(FREQUENCIES.entries()).map(([freq, label]) => (
                                    <DropdownMenuItem
                                        key={freq}
                                        onClick={() => handleFrequencyChange(freq)}
                                        data-tracking-id={`cron-selector-frequency-${freq}`}
                                        className="justify-between"
                                    >
                                        <span>{label}</span>
                                        {config.frequency === freq && <Check className="h-4 w-4" />}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ))}

                {config.frequency === CronFrequency.Hour && (
                    <>
                        <span className="text-sm">at minute</span>
                        {readOnly ? (
                            <span className="text-sm font-medium">{config.minute ?? 0}</span>
                        ) : (
                            <Input
                                type="text"
                                inputMode="numeric"
                                value={minuteInput}
                                onChange={handleMinuteInputChange}
                                onFocus={() => setMinuteFocused(true)}
                                onBlur={handleMinuteInputBlur}
                                className="h-8 w-16 text-sm"
                                data-tracking-id="cron-selector-minute"
                            />
                        )}
                    </>
                )}

                {(config.frequency === CronFrequency.Day ||
                    config.frequency === CronFrequency.Week ||
                    config.frequency === CronFrequency.Month) && (
                    <>
                        {config.frequency === CronFrequency.Week && (
                            <>
                                <span className="text-sm">on</span>
                                {readOnly ? (
                                    <span className="text-sm font-medium">
                                        {WEEKDAYS.get(config.dayOfWeek ?? 0) ?? "Sunday"}
                                    </span>
                                ) : (
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-8 min-w-[120px] border border-border-base"
                                            >
                                                <span>{WEEKDAYS.get(config.dayOfWeek ?? 0) ?? "Sunday"}</span>
                                                <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start">
                                            {Array.from(WEEKDAYS.entries()).map(([value, label]) => (
                                                <DropdownMenuItem
                                                    key={value}
                                                    onClick={() => updateConfig({ dayOfWeek: value })}
                                                    data-tracking-id={`cron-selector-weekday-${value}`}
                                                    className="justify-between"
                                                >
                                                    <span>{label}</span>
                                                    {config.dayOfWeek === value && <Check className="h-4 w-4" />}
                                                </DropdownMenuItem>
                                            ))}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                )}
                            </>
                        )}

                        {config.frequency === CronFrequency.Month && (
                            <>
                                <span className="text-sm">on day</span>
                                {readOnly ? (
                                    <span className="text-sm font-medium">{config.dayOfMonth ?? 1}</span>
                                ) : (
                                    <Input
                                        type="text"
                                        inputMode="numeric"
                                        value={dayOfMonthInput}
                                        onChange={handleDayOfMonthInputChange}
                                        onFocus={() => setDayOfMonthFocused(true)}
                                        onBlur={handleDayOfMonthInputBlur}
                                        className="h-8 w-16 text-sm"
                                        data-tracking-id="cron-selector-day-of-month"
                                    />
                                )}
                            </>
                        )}

                        <span className="text-sm">at</span>
                        <TimePicker
                            value={{ hour: config.hour ?? 0, minute: config.minute ?? 0 }}
                            onChange={(time) => updateConfig({ hour: time.hour, minute: time.minute })}
                            timezone="UTC"
                            showTimezone={showTimezone}
                            readOnly={readOnly}
                        />
                        <LocalTimeDisplay
                            frequency={config.frequency}
                            hour={config.hour ?? 0}
                            minute={config.minute ?? 0}
                            showTimezone={showTimezone}
                        />
                    </>
                )}
            </div>
        </div>
    );
};
